import DataApi from '../api/DataApi';
import {
  splitLoadedReportFields,
  isMatrixMode,
  collectSummarizedMeasureFields,
} from '../components/reports/matrix/matrixReportConfig';
import {
  buildSummarizedDataFromMatrix,
  resolveMatrixLineField,
} from '../components/reports/matrix/matrixChartData';
import { normalizeReportChartForDisplay } from '../components/reports/charts/transformWidgetChart';
import {
  parseMatrixDateConfig,
  buildDateGroupOptions,
  getReportGroupBucket,
} from '../components/reports/matrix/matrixDateGrouping';
import { getAggregatableNumericForExport } from './reportCurrencyAggregate';
import {
  applyDerivedMetricsToDataset,
  collectDerivedMetricRecipes,
} from './derivedMetrics';
import { coerceReportFieldsArray } from '../components/reports/charts/chartPicklistColors';

const convertAggregationValue = (dbValue) => {
  const mapping = {
    average: 'avg',
    avg: 'avg',
    sum: 'sum',
    min: 'min',
    max: 'max',
    count: 'count',
    median: 'median'
  };
  return mapping[dbValue] || dbValue || 'sum';
};

const formatValue = (fieldData, dataType) => {
  if (typeof fieldData === 'object' && fieldData !== null) {
    if (fieldData.isReference && fieldData.displayValue) {
      return fieldData.displayValue;
    }
    if ((fieldData.isPicklist || fieldData.isMultiPicklist) && fieldData.displayValue) {
      return fieldData.displayValue;
    }
    const value = fieldData.value;
    if (value === null || value === undefined || value === '') return 'N/A';

    switch (dataType) {
      case 'currency': {
        const numValue = parseFloat(value);
        if (isNaN(numValue)) return 'N/A';
        return `₹${numValue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      }
      case 'number':
      case 'decimal': {
        const numVal = parseFloat(value);
        if (isNaN(numVal)) return 'N/A';
        return numVal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
      case 'percent': {
        const percentVal = parseFloat(value);
        if (isNaN(percentVal)) return 'N/A';
        return `${percentVal.toFixed(2)}%`;
      }
      default:
        return value;
    }
  }

  if (fieldData === null || fieldData === undefined || fieldData === '') {
    return 'N/A';
  }

  switch (dataType) {
    case 'currency': {
      const numValue = parseFloat(fieldData);
      if (isNaN(numValue)) return 'N/A';
      return `₹${numValue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    case 'number':
    case 'decimal': {
      const numVal = parseFloat(fieldData);
      if (isNaN(numVal)) return 'N/A';
      return numVal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    case 'percent': {
      const percentVal = parseFloat(fieldData);
      if (isNaN(percentVal)) return 'N/A';
      return `${percentVal.toFixed(2)}%`;
    }
    default:
      return fieldData;
  }
};

const calculateAggregation = (data, field, aggregation) => {
  if (!data || data.length === 0) return 'N/A';

  const tableName = field.object_name || 'main';
  const aliasedFieldName = `${tableName}_${field.field_name}`;

  const values = data
    .map(row => {
      const fieldData = row[aliasedFieldName] || row[field.field_name];

      if (typeof fieldData === 'object' && fieldData !== null) {
        const value = fieldData.value;
        return value !== null && value !== undefined && value !== '' ? parseFloat(value) : null;
      }

      return fieldData !== null && fieldData !== undefined && fieldData !== ''
        ? parseFloat(fieldData)
        : null;
    })
    .filter(val => val !== null && !isNaN(val));

  if (values.length === 0) return 'N/A';

  let result;
  switch (aggregation) {
    case 'sum':
      result = values.reduce((sum, val) => sum + val, 0);
      break;
    case 'average':
    case 'avg':
      result = values.reduce((sum, val) => sum + val, 0) / values.length;
      break;
    case 'max':
      result = Math.max(...values);
      break;
    case 'min':
      result = Math.min(...values);
      break;
    case 'median': {
      const sorted = [...values].sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      result = sorted.length % 2 === 0
        ? (sorted[mid - 1] + sorted[mid]) / 2
        : sorted[mid];
      break;
    }
    case 'count':
      return values.length.toString();
    default:
      return 'N/A';
  }

  return formatValue(result, field.data_type);
};

export const transformDatasetToPreviewData = (dataset) => {
  if (!dataset || !dataset._metadata) {
    return [];
  }

  const metadata = dataset._metadata;
  const groupFields = metadata.group_fields || {};
  const summarizedFields = metadata.summarized_fields || {};
  const previewData = [];

  const primaryGroupFieldName = Object.keys(groupFields)[0];
  if (!primaryGroupFieldName) {
    return [];
  }

  const primaryGroupData = dataset[primaryGroupFieldName];
  if (!primaryGroupData) {
    return [];
  }

  Object.keys(primaryGroupData).forEach(groupValue => {
    const groupData = primaryGroupData[groupValue];
    const row = {
      [primaryGroupFieldName]: groupValue
    };

    Object.keys(summarizedFields).forEach(fieldName => {
      if (fieldName === 'record_count') {
        const recordCountValue = groupData?.record_count?.value ?? dataset?.record_count?.[groupValue];
        if (recordCountValue !== undefined) {
          row[fieldName] = recordCountValue;
        }
        return;
      }

      const fieldData = groupData[fieldName];
      if (fieldData && typeof fieldData === 'object' && fieldData.value !== undefined) {
        row[fieldName] = fieldData.value;
      } else if (fieldData !== undefined) {
        row[fieldName] = fieldData;
      }
    });

    previewData.push(row);
  });

  const groupFieldNames = Object.keys(groupFields);
  if (groupFieldNames.length > 1) {
    const additionalRows = [];

    groupFieldNames.slice(1).forEach(groupFieldName => {
      const groupData = dataset[groupFieldName];
      if (groupData) {
        Object.keys(groupData).forEach(groupValue => {
          const fieldData = groupData[groupValue];
          const row = {
            [groupFieldName]: groupValue
          };

          Object.keys(summarizedFields).forEach(fieldName => {
            const summarizedFieldData = fieldData[fieldName];
            if (summarizedFieldData && typeof summarizedFieldData === 'object' && summarizedFieldData.value !== undefined) {
              row[fieldName] = summarizedFieldData.value;
            } else if (summarizedFieldData !== undefined) {
              row[fieldName] = summarizedFieldData;
            }
          });

          additionalRows.push(row);
        });
      }
    });

    previewData.push(...additionalRows);
  }

  return previewData;
};

function normalizePicklistValuesArray(raw) {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const p = JSON.parse(raw);
      return Array.isArray(p) ? p : null;
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Copy picklist_values (incl. path_color) from report field definitions into dataset group_fields
 * so charts can color categories by picklist. Handles array or JSON string; also picklistValues (camelCase).
 */
export function enrichDatasetGroupFieldsWithPicklist(dataset, reportFields) {
  const fields = Array.isArray(reportFields)
    ? reportFields
    : reportFields != null && typeof reportFields === 'object'
      ? Object.values(reportFields)
      : [];
  if (!dataset?._metadata?.group_fields || fields.length === 0) {
    return;
  }
  for (const name of Object.keys(dataset._metadata.group_fields)) {
    const meta = dataset._metadata.group_fields[name];
    const match =
      fields.find(
        f =>
          f.field_name === name && (f.object_name || 'main') === (meta.object_name || 'main')
      ) || fields.find(f => f.field_name === name);
    if (!match) continue;
    const pl =
      normalizePicklistValuesArray(match.picklist_values_data) ||
      normalizePicklistValuesArray(match.picklist_values) ||
      normalizePicklistValuesArray(match.picklistValues);
    if (pl && pl.length > 0) {
      dataset._metadata.group_fields[name] = {
        ...meta,
        picklist_values: pl,
        data_type: meta.data_type || match.data_type
      };
    }
  }
}

export const extractReportFieldMetadata = (fields = []) => {
  const { rowGroups, columnGroups, selectedFieldsList, summarized } = splitLoadedReportFields(
    fields,
    convertAggregationValue
  );

  return {
    selectedFields: selectedFieldsList,
    selectedGroups: rowGroups,
    selectedColumnGroups: columnGroups,
    summarizedFields: summarized,
    isMatrixReport: isMatrixMode(columnGroups),
  };
};

/** Split cached dataset group_fields into row vs column groups for matrix charts. */
export function splitGroupsFromDatasetMetadata(groupFields = {}) {
  const rowGroups = [];
  const columnGroups = [];

  Object.entries(groupFields).forEach(([fieldName, info]) => {
    const entry = {
      id: info.field_id,
      field_name: fieldName,
      field_label: info.field_label || fieldName,
      data_type: info.data_type,
      object_name: info.object_name,
      is_grouped: true,
      group_axis: info.group_axis || 'row',
    };
    if (String(info.group_axis || 'row').toLowerCase() === 'column') {
      columnGroups.push(entry);
    } else {
      rowGroups.push(entry);
    }
  });

  return { rowGroups, columnGroups };
}

export function applyMatrixMetadataToDataset(dataset, rowGroups, columnGroups) {
  if (!dataset?._metadata || !isMatrixMode(columnGroups)) {
    return dataset;
  }

  const meta = dataset._metadata;
  meta.report_info = {
    ...(meta.report_info || {}),
    report_type: 'matrix',
    row_group_count: rowGroups.length,
    column_group_count: columnGroups.length,
  };

  rowGroups.forEach((f) => {
    if (meta.group_fields[f.field_name]) {
      meta.group_fields[f.field_name] = {
        ...meta.group_fields[f.field_name],
        group_axis: 'row',
      };
    }
  });

  columnGroups.forEach((f) => {
    if (meta.group_fields[f.field_name]) {
      meta.group_fields[f.field_name] = {
        ...meta.group_fields[f.field_name],
        group_axis: 'column',
      };
    }
  });

  return dataset;
}

/** Merge API grouping fields with report field defs (data_type needed for date bucketing). */
function enrichGroupedFieldsForChart(groupedFields = [], selectedGroups = [], selectedFields = []) {
  const lookup = [...(selectedGroups || []), ...(selectedFields || [])];
  return groupedFields.map((gf) => {
    const match = lookup.find(
      (f) =>
        f.field_name === gf.field_name &&
        (f.object_name || 'main') === (gf.object_name || 'main')
    );
    if (!match) return gf;
    return {
      ...gf,
      id: gf.id || match.id || match.field_config_id,
      field_config_id: gf.field_config_id || match.field_config_id || match.id,
      data_type: gf.data_type || match.data_type,
      field_type: gf.field_type || match.field_type,
    };
  });
}

const buildSummarizedDataset = (rawData, grouping, fieldMetadata, dateGroupOptions = null) => {
  if (!grouping || !grouping.hasGrouping || !rawData || rawData.length === 0) {
    return {};
  }

  const dateOpts =
    dateGroupOptions ||
    buildDateGroupOptions(
      fieldMetadata.matrixDateGranularity,
      fieldMetadata.dateGranularityByFieldId
    );

  const groupedFields = enrichGroupedFieldsForChart(
    grouping.groupedFields || [],
    fieldMetadata.selectedGroups,
    fieldMetadata.selectedFields
  );
  const allFields = fieldMetadata.selectedFields || [];
  const summarizedFieldMap = fieldMetadata.summarizedFields || {};

  const fieldsWithAggregation = collectSummarizedMeasureFields(
    allFields,
    summarizedFieldMap,
    groupedFields
  );

  const summarizedData = {};
  summarizedData._metadata = {
    group_fields: {},
    summarized_fields: {},
    report_info: {
      total_records: rawData.length,
      group_count: groupedFields.length,
      summarized_field_count: Object.keys(summarizedFieldMap).length,
      extraction_timestamp: new Date().toISOString(),
      data_structure_version: '2.0'
    }
  };

  groupedFields.forEach(field => {
    summarizedData._metadata.group_fields[field.field_name] = {
      field_id: field.field_config_id || field.id || `${field.object_name || 'main'}.${field.field_name}`,
      field_name: field.field_name,
      field_label: field.field_label || field.field_name,
      data_type: field.data_type,
      object_name: field.object_name,
      field_type: field.field_type
    };
  });

  collectSummarizedMeasureFields(
    allFields,
    summarizedFieldMap,
    groupedFields
  ).forEach((field) => {
    summarizedData._metadata.summarized_fields[field.field_name] = {
      field_id: field.id,
      field_name: field.field_name,
      field_label: field.field_label,
      data_type: field.data_type,
      object_name: field.object_name,
      aggregation: field.aggregation,
    };
  });

  if (groupedFields.length > 0) {
    summarizedData._metadata.summarized_fields.record_count = {
      field_id: 'record_count',
      field_name: 'record_count',
      field_label: 'Record Count',
      data_type: 'number',
      object_name: 'virtual',
      aggregation: 'count',
      is_virtual: true
    };
  }

  if (groupedFields.length > 0) {
    groupedFields.forEach((primaryGroupField, idx) => {
      const groupFieldName = primaryGroupField.field_name;
      const groupValues = new Set();

      rawData.forEach((row) => {
        const { label } = getReportGroupBucket(primaryGroupField, row, dateOpts);
        groupValues.add(label || 'N/A');
      });

      if (idx === 0) {
        fieldsWithAggregation.forEach((field) => {
          if (!summarizedData[field.field_name]) {
            summarizedData[field.field_name] = {};
          }
        });
        if (!summarizedData.record_count) {
          summarizedData.record_count = {};
        }
      }

      groupValues.forEach((groupValue) => {
        const groupData = rawData.filter((row) => {
          const { label } = getReportGroupBucket(primaryGroupField, row, dateOpts);
          return (label || 'N/A') === groupValue;
        });

        fieldsWithAggregation.forEach((field) => {
          const aggregatedValue = calculateAggregation(
            groupData,
            field,
            field.aggregation
          );
          let chartValue = aggregatedValue;

          if (field.data_type === 'currency' && typeof aggregatedValue === 'string') {
            const numericValue = parseFloat(aggregatedValue.replace(/[₹,]/g, ''));
            chartValue = isNaN(numericValue) ? 0 : numericValue;
          } else if (field.data_type === 'number' || field.data_type === 'decimal') {
            const numericValue = parseFloat(aggregatedValue);
            chartValue = isNaN(numericValue) ? 0 : numericValue;
          }

          summarizedData[field.field_name][groupValue] = chartValue;
        });

        const recordCount = groupData.length;
        summarizedData.record_count[groupValue] = recordCount;
      });

      summarizedData[groupFieldName] = {};
      groupValues.forEach(groupValue => {
        summarizedData[groupFieldName][groupValue] = {};
        fieldsWithAggregation.forEach((field) => {
          if (summarizedData[field.field_name]?.[groupValue] !== undefined) {
            summarizedData[groupFieldName][groupValue][field.field_name] = {
              value: summarizedData[field.field_name][groupValue],
            };
          }
        });

        if (summarizedData.record_count[groupValue] !== undefined) {
          summarizedData[groupFieldName][groupValue].record_count = {
            value: summarizedData.record_count[groupValue]
          };
        }
      });
    });
  }

  return summarizedData;
};

const getRowFieldData = (row, field) => {
  const tableName = field.object_name || 'main';
  const aliasedFieldName = `${tableName}_${field.field_name}`;
  return row?.[aliasedFieldName] ?? row?.[field.field_name];
};

const extractNumericValue = (value) => {
  if (value == null) return null;
  if (typeof value === 'object') {
    const v = value.value ?? value.displayValue;
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : null;
  }
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : null;
};

const getRowMeasureNumeric = (row, field, currencyOptions = {}) => {
  const { baseCurrencyCode = null, exchangeRateLookup = null, multiCurrencyEnabled = true } =
    currencyOptions;
  const conversionBase = multiCurrencyEnabled ? baseCurrencyCode : null;
  const conversionLookup = multiCurrencyEnabled ? exchangeRateLookup : null;

  return getAggregatableNumericForExport(
    field,
    row,
    conversionBase,
    (f, r) => {
      const fieldData = getRowFieldData(r, f);
      if (typeof fieldData === 'object' && fieldData !== null) {
        const v = fieldData.value ?? fieldData.displayValue;
        return v !== undefined && v !== null ? v : '';
      }
      return fieldData;
    },
    conversionLookup
  );
};

const buildSummarizedDatasetFromGroupedRows = (
  rawData,
  grouping,
  fieldMetadata,
  currencyOptions = {},
  dateGroupOptions = null
) => {
  const dateOpts =
    dateGroupOptions ||
    buildDateGroupOptions(
      fieldMetadata.matrixDateGranularity,
      fieldMetadata.dateGranularityByFieldId
    );

  const groupedFields = enrichGroupedFieldsForChart(
    grouping?.groupedFields || [],
    fieldMetadata.selectedGroups,
    fieldMetadata.selectedFields
  );
  if (!grouping?.hasGrouping || groupedFields.length === 0 || !Array.isArray(rawData) || rawData.length === 0) {
    return {};
  }

  const allFields = fieldMetadata.selectedFields || [];
  const summarizedFieldMap = fieldMetadata.summarizedFields || {};
  const fieldsWithAggregation = collectSummarizedMeasureFields(
    allFields,
    summarizedFieldMap,
    groupedFields
  );

  const summarizedData = {
    _metadata: {
      group_fields: {},
      summarized_fields: {},
      report_info: {
        total_records: rawData.length,
        group_count: groupedFields.length,
        summarized_field_count: Object.keys(summarizedFieldMap).length,
        extraction_timestamp: new Date().toISOString(),
        data_structure_version: '2.0'
      }
    }
  };

  groupedFields.forEach(field => {
    summarizedData._metadata.group_fields[field.field_name] = {
      field_id: field.field_config_id || field.id || `${field.object_name || 'main'}.${field.field_name}`,
      field_name: field.field_name,
      field_label: field.field_label || field.field_name,
      data_type: field.data_type,
      object_name: field.object_name,
      field_type: field.field_type
    };
  });

  fieldsWithAggregation.forEach((field) => {
    summarizedData._metadata.summarized_fields[field.field_name] = {
      field_id: field.id,
      field_name: field.field_name,
      field_label: field.field_label,
      data_type: field.data_type,
      object_name: field.object_name,
      aggregation: field.aggregation,
    };
    summarizedData[field.field_name] = {};
  });

  summarizedData._metadata.summarized_fields.record_count = {
    field_id: 'record_count',
    field_name: 'record_count',
    field_label: 'Record Count',
    data_type: 'number',
    object_name: 'virtual',
    aggregation: 'count',
    is_virtual: true
  };
  summarizedData.record_count = {};

  groupedFields.forEach(primaryGroupField => {
    const groupFieldName = primaryGroupField.field_name;
    summarizedData[groupFieldName] = {};

    rawData.forEach((row) => {
      const { label } = getReportGroupBucket(primaryGroupField, row, dateOpts);
      const groupValue = label || 'N/A';
      if (!summarizedData[groupFieldName][groupValue]) {
        summarizedData[groupFieldName][groupValue] = {};
      }

      fieldsWithAggregation.forEach((field) => {
        const n = getRowMeasureNumeric(row, field, currencyOptions);
        if (n == null || !Number.isFinite(n)) return;
        if (field.aggregation === 'sum' || !field.aggregation) {
          summarizedData[field.field_name][groupValue] =
            (summarizedData[field.field_name][groupValue] || 0) + n;
        } else {
          summarizedData[field.field_name][groupValue] = n;
        }
        summarizedData[groupFieldName][groupValue][field.field_name] = {
          value: summarizedData[field.field_name][groupValue],
        };
      });

      const explicitRecordCount = extractNumericValue(row?.record_count ?? row?.main_record_count);
      const countValue = explicitRecordCount != null ? explicitRecordCount : 1;
      summarizedData.record_count[groupValue] = (summarizedData.record_count[groupValue] || 0) + countValue;
      summarizedData[groupFieldName][groupValue].record_count = {
        value: summarizedData.record_count[groupValue]
      };
    });
  });

  return summarizedData;
};

/**
 * Build chart dataset + preview rows from in-memory report data (widget builder preview).
 * Mirrors {@link fetchReportChartData} enrichment: matrix metadata, picklist colors, formula metrics.
 */
export function buildChartDatasetForPreview({
  rawData = [],
  groupingInfo = null,
  selectedFields = [],
  selectedGroups = [],
  selectedColumnGroups = [],
  summarizedFields = {},
  reportFields = [],
  reportConfig = null,
  reportCharts = [],
  chartConfig = null,
  baseCurrencyCode = null,
  exchangeRateLookup = null,
  multiCurrencyEnabled = true,
}) {
  const reportFieldList = coerceReportFieldsArray(reportFields);
  const currencyOptions = {
    baseCurrencyCode: multiCurrencyEnabled ? baseCurrencyCode : null,
    exchangeRateLookup: multiCurrencyEnabled ? exchangeRateLookup : null,
    multiCurrencyEnabled,
  };

  if (!rawData?.length) {
    return { dataset: {}, previewData: [], isMatrixReport: false };
  }

  const isMatrixReport = isMatrixMode(selectedColumnGroups);
  const matrixFromApi = groupingInfo?.hasMatrix === true || isMatrixReport;

  const matrixDateCfg = parseMatrixDateConfig(reportConfig);
  const matrixDateGranularity = matrixDateCfg.matrixDateGranularity || 'month';
  const dateGroupOptions = buildDateGroupOptions(
    matrixDateGranularity,
    matrixDateCfg.dateGranularityByFieldId
  );

  const fieldMetadata = {
    selectedFields,
    summarizedFields,
    selectedGroups,
    matrixDateGranularity,
    dateGranularityByFieldId: matrixDateCfg.dateGranularityByFieldId,
  };

  let dataset = groupingInfo?.hasGrouping
    ? buildSummarizedDatasetFromGroupedRows(
        rawData,
        groupingInfo,
        fieldMetadata,
        currencyOptions,
        dateGroupOptions
      )
    : buildSummarizedDataset(rawData, groupingInfo, fieldMetadata, dateGroupOptions);

  if (matrixFromApi) {
    const matrixMeta = buildSummarizedDataFromMatrix({
      previewData: rawData,
      rowGroups: selectedGroups,
      columnGroups: selectedColumnGroups,
      selectedFields,
      summarizedFields,
    });
    if (matrixMeta?._metadata) {
      dataset = {
        ...dataset,
        _metadata: {
          ...(dataset?._metadata || {}),
          ...matrixMeta._metadata,
          group_fields: {
            ...(dataset?._metadata?.group_fields || {}),
            ...matrixMeta._metadata.group_fields,
          },
          summarized_fields: {
            ...(dataset?._metadata?.summarized_fields || {}),
            ...matrixMeta._metadata.summarized_fields,
          },
        },
      };
    }
    dataset = applyMatrixMetadataToDataset(dataset, selectedGroups, selectedColumnGroups);
  }

  enrichDatasetGroupFieldsWithPicklist(dataset, reportFieldList);

  const derivedRecipes = collectDerivedMetricRecipes({
    reportConfig,
    charts: reportCharts,
    chartConfig,
    defaultGroupFieldName: selectedGroups?.[0]?.field_name,
  });
  if (derivedRecipes.length > 0) {
    dataset = applyDerivedMetricsToDataset(dataset, derivedRecipes, {
      previewData: rawData,
      selectedFields,
      summarizedFields,
      selectedGroups,
      rowGroups: selectedGroups,
      columnGroups: selectedColumnGroups,
      matrixDateGranularity,
      dateOptions: dateGroupOptions,
    });
  }

  const previewData =
    matrixFromApi && rawData.length
      ? rawData
      : Object.keys(dataset || {}).length > 0
        ? transformDatasetToPreviewData(dataset)
        : rawData;

  return {
    dataset,
    previewData,
    isMatrixReport: matrixFromApi,
  };
}

export const fetchReportChartData = async ({
  reportId,
  chartId,
  limit = 10000,
  filters = {},
  existingChartConfig = null,
  runningUserId = null,
  dashboardId = null,
  viewAsUserId = null,
  baseCurrencyCode = null,
  exchangeRateLookup = null,
  multiCurrencyEnabled = true,
}) => {
  if (!reportId) {
    throw new Error('reportId is required to fetch report chart data');
  }

  const reportBuilderApi = new DataApi('reportbuilder');
  const reportResponse = await reportBuilderApi.get(`/${reportId}`);

  if (!reportResponse?.data?.success) {
    throw new Error(reportResponse?.data?.message || 'Failed to load report configuration');
  }

  const report = reportResponse.data.data || {};
  const fieldMetadata = extractReportFieldMetadata(report.fields || []);
  const {
    selectedFields,
    selectedGroups,
    selectedColumnGroups,
    summarizedFields,
  } = fieldMetadata;

  let chartConfig = existingChartConfig || null;
  if (report.charts && report.charts.length > 0) {
    if (chartId) {
      chartConfig = report.charts.find(chart => chart.id === chartId) || chartConfig;
    }
    if (!chartConfig) {
      chartConfig = report.charts[0];
    }
  }

  const dataApi = new DataApi('object');
  const viewPayload = {
    reportId,
    limit,
    filters,
  };
  if (runningUserId) {
    viewPayload.runningUserId = runningUserId;
  }
  if (dashboardId) {
    viewPayload.dashboardId = dashboardId;
  }
  if (viewAsUserId) {
    viewPayload.viewAsUserId = viewAsUserId;
  }
  const dataResponse = await dataApi.post('/view/report', viewPayload);

  if (!dataResponse?.data?.success) {
    throw new Error(dataResponse?.data?.message || 'Failed to load report data');
  }

  const rawData = dataResponse.data.data || [];
  const groupingInfo = dataResponse.data.grouping || null;

  const { dataset, previewData, isMatrixReport: matrixFromApi } = buildChartDatasetForPreview({
    rawData,
    groupingInfo,
    selectedFields,
    selectedGroups,
    selectedColumnGroups,
    summarizedFields,
    reportFields: coerceReportFieldsArray(report.fields),
    reportConfig: report.config,
    reportCharts: report.charts || [],
    chartConfig,
    baseCurrencyCode,
    exchangeRateLookup,
    multiCurrencyEnabled,
  });

  if (chartConfig) {
    chartConfig = normalizeReportChartForDisplay(chartConfig, dataset);
  }

  return {
    report,
    chartConfig,
    rawData,
    dataset,
    previewData,
    groupingInfo,
    selectedFields,
    summarizedFields,
    selectedGroups,
    selectedColumnGroups,
    isMatrixReport: matrixFromApi,
  };
};

/** True when a combo chart needs line data that is missing from a cached widget dataset. */
export function chartDatasetMissingDerivedLine(dataset, chart) {
  if (!dataset || !chart) return false;
  const normalized = normalizeReportChartForDisplay(chart, dataset);
  const lineField = resolveMatrixLineField(normalized, dataset);
  if (!lineField) return false;
  const xField = normalized.xAxisField || normalized.selectedCategoryField;
  if (!xField || !dataset[xField]) return true;
  return !Object.keys(dataset[xField]).some((cat) => {
    const cell = dataset[xField][cat]?.[lineField];
    if (cell == null) return false;
    const v = cell.value;
    return v != null && v !== '';
  });
}

export function isComboChartConfig(chart) {
  if (!chart) return false;
  const ct = String(chart.chartType || chart.chart_type || '').toLowerCase();
  return Boolean(
    chart.stackByField ||
      chart.stack_by_field ||
      chart.matrixLineField ||
      chart.matrix_line_field ||
      chart.matrixChart ||
      chart.matrix_chart ||
      ct === 'mixed'
  );
}

export { applyDerivedMetricsToDataset, collectDerivedMetricRecipes } from './derivedMetrics';
export default fetchReportChartData;
